import { type ReactNode, useCallback, useMemo } from 'react';

import {
	type AvailabilityContext,
	isFieldAllowed,
	isFieldAvailable
} from '@/core/dcr/fields/availability';
import type { CommonFieldDef } from '@/core/dcr/fields/types';
import { getPicklistOptions, type Picklists } from '@/core/dcr/picklists';
import type { EntityType } from '@/core/graphql/generated/types';
import { usePermissionCheck } from '@/core/store/auth/authSelectors';

import {
	DcrFormContext,
	type DcrFormContextValue
} from './_context/DcrFormContext';
import { useDisclosure } from './_hooks/useDisclosure';

type Props = {
	entityType: EntityType;
	countryId: string | null | undefined;
	picklists: Partial<Picklists>;
	unavailable: ReadonlySet<string>;
	children: ReactNode;
};

export const DcrFormFieldsProvider = ({
	entityType,
	countryId,
	picklists,
	unavailable,
	children
}: Props) => {
	const hasPermission = usePermissionCheck();
	const disclosure = useDisclosure();

	const availability: AvailabilityContext = useMemo(
		() => ({
			countryId,
			unavailable,
			hasPermission,
			hasOptions: source => getPicklistOptions(picklists, source).length > 0
		}),
		[countryId, hasPermission, picklists, unavailable]
	);

	const isAvailable = useCallback(
		(id: string, def: CommonFieldDef) =>
			isFieldAvailable(id, def, availability),
		[availability]
	);

	const isAllowed = useCallback(
		(id: string, def: CommonFieldDef) => isFieldAllowed(id, def, availability),
		[availability]
	);

	const value: DcrFormContextValue = useMemo(
		() => ({
			entityType,
			countryId: countryId ?? undefined,
			picklists,
			unavailable,
			isAvailable,
			isAllowed,
			disclosure
		}),
		[
			countryId,
			disclosure,
			entityType,
			isAllowed,
			isAvailable,
			picklists,
			unavailable
		]
	);

	return (
		<DcrFormContext.Provider value={value}>{children}</DcrFormContext.Provider>
	);
};
